import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Spinner } from '../common';
import { FeedbackImageIcon } from '../svg';
import { validateFeedbackImage } from '../../helpers/fileHelpers/feedbackScreenshotHelper';

const formatBytes = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// One image only — a second pick replaces the first. The upload to S3 starts as soon as a file is
// picked (T10); `uploadStatus` reflects that background upload and Removing cancels it (the parent
// discards the in-flight result), so attaching then cancelling leaves nothing referenced.
const FeedbackAttachment = ({ file, onChange, onRetry, uploadStatus, imageMaxMb, disabled }) => {
  const { t } = useTranslation();
  const inputRef = useRef();
  const [error, setError] = useState(null);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    if (!file) {
      setPreview(null);
      return undefined;
    }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const handlePick = (e) => {
    const picked = e.target.files?.[0];
    e.target.value = '';
    if (!picked) return;
    const { ok, errorKey } = validateFeedbackImage(picked, imageMaxMb);
    if (!ok) {
      setError(errorKey);
      return;
    }
    setError(null);
    onChange(picked);
  };

  const handleRemove = () => {
    setError(null);
    onChange(null);
  };

  if (file) {
    const uploading = uploadStatus === 'uploading';
    const failed = uploadStatus === 'error';
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          border: `1px solid ${failed ? '#FFCCC7' : '#E0E0E0'}`,
          borderRadius: 10,
          padding: 8,
        }}
      >
        <img src={preview} alt="" style={{ width: 40, height: 40, borderRadius: 6, objectFit: 'cover' }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: '#222',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {file.name}
          </div>
          <div
            style={{
              fontSize: 12,
              color: failed ? '#CF1322' : '#707070',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            {uploading && <Spinner size="small" />}
            {uploading ? t('Uploading…') : failed ? t('Upload failed') : formatBytes(file.size)}
          </div>
        </div>
        {failed && (
          <button
            type="button"
            onClick={onRetry}
            disabled={disabled}
            style={{
              background: 'none',
              border: 'none',
              color: '#006169',
              fontSize: 13,
              fontWeight: 600,
              cursor: disabled ? 'default' : 'pointer',
              padding: 0,
            }}
          >
            {t('Retry')}
          </button>
        )}
        <button
          type="button"
          onClick={handleRemove}
          disabled={disabled}
          style={{
            background: 'none',
            border: 'none',
            color: '#006169',
            fontSize: 13,
            fontWeight: 600,
            cursor: disabled ? 'default' : 'pointer',
            padding: 0,
          }}
        >
          {t('Remove')}
        </button>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        onClick={() => !disabled && inputRef.current?.click()}
        onKeyDown={(e) => {
          if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            inputRef.current?.click();
          }
        }}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          border: '1px dashed #CFD6D7',
          borderRadius: 10,
          padding: 12,
          color: '#006169',
          fontSize: 13,
          fontWeight: 600,
          cursor: disabled ? 'default' : 'pointer',
          background: '#F8FBFB',
          opacity: disabled ? 0.6 : 1,
        }}
      >
        <FeedbackImageIcon size={18} />
        {t('Attach a screenshot')}
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          onChange={handlePick}
          disabled={disabled}
          style={{ display: 'none' }}
        />
      </div>
      {error && <div style={{ fontSize: 12, color: '#D4380D' }}>{t(error, { max: imageMaxMb })}</div>}
    </div>
  );
};

export default FeedbackAttachment;
